import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart, faCheckCircle, faMoneyBillWave, faUsers, faCoins,
  faBoxes, faTicket, faClock, faExclamationTriangle, faSync, faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext';
import './AdminDashboard.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const SYNC_INTERVAL = 3 * 60 * 1000;

const iconMap = {
  total_orders: faShoppingCart,
  completed_orders: faCheckCircle,
  pending_orders: faClock,
  cancelled_orders: faExclamationTriangle,
  total_revenue: faMoneyBillWave,
  total_users: faUsers,
  total_user_balance: faCoins,
  active_services: faBoxes,
  open_tickets: faTicket,
};

const colorMap = {
  total_orders: 'accent',
  completed_orders: 'green',
  pending_orders: 'orange',
  cancelled_orders: 'red',
  total_revenue: 'accent',
  total_users: 'green',
  total_user_balance: 'accent',
  active_services: 'orange',
  open_tickets: 'red',
};

const labelMap = {
  total_orders: 'Total Orders',
  completed_orders: 'Completed',
  pending_orders: 'Pending',
  cancelled_orders: 'Cancelled',
  total_revenue: 'Total Revenue',
  total_users: 'Total Users',
  total_user_balance: 'User Balances',
  active_services: 'Active Services',
  open_tickets: 'Open Tickets',
};

const formatValue = (key, val) => {
  if (key === 'total_revenue' || key === 'total_user_balance') return `TSHS ${Number(val || 0).toLocaleString()}`;
  return Number(val || 0).toLocaleString();
};

const statusColors = {
  completed: 'status-completed',
  processing: 'status-processing',
  pending: 'status-pending',
  cancelled: 'status-cancelled',
};

const priorityColors = {
  high: 'priority-high',
  medium: 'priority-medium',
  low: 'priority-low',
};

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const refreshAll = useCallback(() => {
    fetch(`${API}/admin/dashboard`, { headers })
      .then((r) => r.json())
      .then((data) => setStats(data || {}))
      .catch(() => {});

    fetch(`${API}/admin/orders?per_page=5`, { headers })
      .then((r) => r.json())
      .then((data) => setRecentOrders(Array.isArray(data) ? data.slice(0, 5) : []))
      .catch(() => {});

    fetch(`${API}/admin/tickets?per_page=5`, { headers })
      .then((r) => r.json())
      .then((data) => setRecentTickets(Array.isArray(data) ? data.slice(0, 5) : []))
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncStatuses = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/admin/orders/sync-status`, { method: 'POST', headers });
      await res.json();
    } catch {
      // ignore sync errors
    } finally {
      setLastSync(new Date());
      setSyncing(false);
      refreshAll();
    }
  }, [refreshAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshAll();
    syncStatuses();
    const interval = setInterval(syncStatuses, SYNC_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshAll, syncStatuses]);

  return (
    <div className="admin-dashboard">
      <div className="dashboard-sync-bar">
        <div className="dashboard-sync-info">
          <FontAwesomeIcon icon={faSync} className={syncing ? 'spinning' : ''} />
          <span>{syncing ? 'Syncing order statuses...' : 'Auto-sync enabled'}</span>
          {lastSync && <span className="dashboard-sync-last">Last synced {lastSync.toLocaleTimeString()}</span>}
        </div>
        <button className="sync-btn" onClick={syncStatuses} disabled={syncing}>
          <FontAwesomeIcon icon={faSync} className={syncing ? 'spinning' : ''} />
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      <div className="admin-stats-grid">
        {Object.keys(labelMap).map((key) => (
          <div key={key} className={`admin-stat-card ${colorMap[key]}`}>
            <div className="admin-stat-icon">
              <FontAwesomeIcon icon={iconMap[key]} />
            </div>
            <div className="admin-stat-info">
              <span className="admin-stat-value">{formatValue(key, stats[key])}</span>
              <span className="admin-stat-label">{labelMap[key]}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-grid">
        <div className="admin-card">
          <h2 className="admin-card-title">Recent Orders</h2>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o) => (
                  <tr key={o.id}>
                    <td className="id-cell">#{o.id}</td>
                    <td>{o.user?.name || '—'}</td>
                    <td>{o.service?.name || '—'}</td>
                    <td>TSHS {Number(o.charge || 0).toLocaleString()}</td>
                    <td><span className={`status-badge ${statusColors[o.status] || ''}`}>{o.status}</span></td>
                    <td className="date-cell">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr><td colSpan={6} className="empty-cell">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Recent Tickets</h2>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Subject</th>
                  <th>Priority</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.map((t) => (
                  <tr key={t.id}>
                    <td className="id-cell">#{t.id}</td>
                    <td>{t.user?.name || '—'}</td>
                    <td>{t.subject}</td>
                    <td><span className={`priority-badge ${priorityColors[t.priority] || ''}`}>{t.priority}</span></td>
                    <td className="date-cell">{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {recentTickets.length === 0 && (
                  <tr><td colSpan={5} className="empty-cell">No tickets yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
